import { Alert, AlertTitle, Paper } from '@mui/material'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import FullPageSpinner from '@/components/feedback/FullPageSpinner'
import { useAlertContext } from '@/contexts/AlertContext'
import { useAuth } from '@/features/authentication/contexts/AuthContext'
import { ControllerRestartProvider } from '@/features/controller/restart/contexts/ControllerRestartContext'
import ResourceCard from '@/features/resource/components/ResourceCard'
import { ResourceJsonEditor } from '@/features/resource/configuration/components/ResourceJsonEditor'
import { ResourceJsonErrorAlert } from '@/features/resource/configuration/components/ResourceJsonErrorAlert'
import { ResourceJsonFileProvider } from '@/features/resource/configuration/contexts/ResourceJsonFileContext'
import {
  useInvalidateResourceJsonQuery,
  useResourceJsonQuery,
} from '@/features/resource/configuration/hooks/useResourceJsonQuery'
import { isResourceConfigurationTabVisible } from '@/features/resource/configuration/utils/resourceConfigurationVisibility'
import { ResourceRouteInformation } from '@/features/resource/constants'
import { useResourceData } from '@/features/resource/contexts/ResourceDataContext'
import { useResourceDetails } from '@/features/resource/pages/ResourceDetailsPage'

function ResourceConfigurationPage() {
  const { t } = useTranslation()
  const { resourceUuid } = useParams()
  const { setActivePage } = useResourceDetails()
  const { resource } = useResourceData()
  const { loggedInUser } = useAuth()
  const { pushAlert } = useAlertContext()

  // The route is mounted only for admins/traders, so set the active tab eagerly to avoid the wrong
  // tab flashing as highlighted while the resource (and thus the full visibility check) is loading.
  useEffect(() => {
    setActivePage(ResourceRouteInformation.RESOURCE_CONFIGURATION.routePath)
  }, [setActivePage])

  const isVisible = isResourceConfigurationTabVisible({
    resource,
    loggedInUser,
  })

  if (!resourceUuid) {
    return null
  }

  // ResourceCard reads the resource itself via useResourceData and renders null until it arrives,
  // so wrap the page body in it to pick up the shared CardHeader + general-info section the other
  // resource tabs (Custom attributes, Support toolbox) already show. The `content` prop is only
  // rendered once the resource is in place, so `resource` is guaranteed non-null inside it.
  const batteryControlPortId =
    resource?.controlPorts.find((port) => port.controlBoxType === 'BATTERY')?.controlPortID ?? null

  const content = !isVisible ? (
    <Paper sx={{ p: 3 }}>{t('resources.configuration.not_available')}</Paper>
  ) : (
    <ControllerRestartProvider>
      <ResourceJsonFileProvider resourceId={resourceUuid}>
        <ResourceConfigurationContent
          batteryControlPortId={batteryControlPortId}
          resourceId={resourceUuid}
          onConflictHint={() => pushAlert({ message: t('resources.configuration.refresh_hint'), severity: 'info' })}
        />
      </ResourceJsonFileProvider>
    </ControllerRestartProvider>
  )

  // `showGeneralInfo={false}` keeps the shared CardHeader (resource name + LOCAL/CLOUD chip) but
  // skips the ResourceDetailTable directly under it — BindingPanel already surfaces id / type /
  // batteryRef, so the table would just duplicate that.
  return <ResourceCard content={content} showGeneralInfo={false} />
}

type ResourceConfigurationContentProps = {
  resourceId: string
  batteryControlPortId: string | null
  onConflictHint: () => void
}

function ResourceConfigurationContent({
  resourceId,
  batteryControlPortId,
  onConflictHint,
}: Readonly<ResourceConfigurationContentProps>) {
  const { t } = useTranslation()
  const { resourceJsonConfig, errorCode, isLoading, isError } = useResourceJsonQuery(resourceId)
  const invalidate = useInvalidateResourceJsonQuery()

  if (isLoading) {
    return <FullPageSpinner />
  }

  // The BFF GET rejected (distinct from the 404 "no config yet" path below). Surface the dedicated
  // alert for known business codes (CONTROLLER_NOT_FOUND etc.); fall back to a generic load error
  // for network/5xx/unknown failures. `controllerId` is unknown on the GET error path → null.
  if (isError) {
    return (
      <Paper sx={{ p: 3 }}>
        {errorCode ? (
          <ResourceJsonErrorAlert code={errorCode} controllerId={null} />
        ) : (
          // Network/5xx/unknown failure: same styled, announced Alert as the known-code path.
          <Alert severity="error">
            <AlertTitle>{t('resources.configuration.error.title')}</AlertTitle>
            {t('resources.configuration.load_error')}
          </Alert>
        )}
      </Paper>
    )
  }

  if (!resourceJsonConfig) {
    return <Paper sx={{ p: 3 }}>{t('resources.configuration.not_found')}</Paper>
  }

  const handleConflict = () => {
    onConflictHint()
    void invalidate(resourceId)
  }

  return (
    <ResourceJsonEditor
      batteryControlPortId={batteryControlPortId}
      configVersion={resourceJsonConfig.configurationVersion}
      controllerId={resourceJsonConfig.controllerId}
      resourceId={resourceId}
      onConflict={handleConflict}
    />
  )
}

export default ResourceConfigurationPage
